import { Button, Checkbox, Modal } from "@arco-design/web-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { isGenericHttpFailureMessage, normalizeHttpError } from "../../../lib/http/errors";
import type { PriceReusePreview } from "../types";

/** 最新行程重建弹窗属性。 */
export type QuoteRebuildFromItineraryDialogProps = {
  visible: boolean;
  preview?: PriceReusePreview;
  loadingPreview: boolean;
  previewError: unknown;
  creating: boolean;
  createError: unknown;
  hasCreatedVersion: boolean;
  hasStartedRequest: boolean;
  fixedReuseExactMatchedPrices: boolean | null;
  blockedReason?: string;
  onRetryPreview: () => void | Promise<void>;
  onConfirm: (reuseExactMatchedPrices: boolean) => Promise<void>;
  onCancel: () => void;
};

/** 把重建失败转换成可执行的中文恢复提示。 */
function rebuildErrorText(error: unknown): string | null {
  if (!error) return null;
  const normalized = normalizeHttpError(error);
  if (normalized.statusCode === 403) {
    return "当前账号没有生成核价版本的权限，请联系管理员。";
  }
  if (normalized.code === "QUOTE_BUDGET_VERSION_CONFLICT") {
    return "其他用户刚刚生成了新版本，请重试。";
  }
  if (normalized.code === "QUOTE_BUDGET_SCHEMA_NOT_READY") {
    return "核价数据库结构尚未完成升级，请联系管理员完成升级后重试。";
  }
  if (normalized.code === "QUOTE_BUDGET_ITINERARY_CHANGED") {
    return "当前行程已发生变化，请刷新后重新生成。";
  }
  const message = normalized.message.trim();
  const hasChinese = /[\u3400-\u9fff]/u.test(message);
  const containsTechnicalDetail = /https?:\/\/|request failed|status code|trace|stack|exception|gateway|timeout|sql/i.test(message);
  if (message && !isGenericHttpFailureMessage(message) && hasChinese && !containsTechnicalDetail && message.length <= 120) {
    return message;
  }
  return "核价版本生成失败，请稍后重试。";
}

/** 已创建版本的加载失败必须保留创建事实，同时隐藏底层网络细节。 */
function createdVersionLoadErrorText(error: unknown): string | null {
  if (!error) return null;
  const rawMessage = error instanceof Error ? error.message.trim() : String(error).trim();
  const createdPrefix = rawMessage.match(/^新版本 V\d+ 已生成，但加载失败/u)?.[0];
  const containsTechnicalDetail = /https?:\/\/|request failed|status code|trace|stack|exception|gateway|timeout|sql/i.test(rawMessage);
  return createdPrefix && containsTechnicalDetail
    ? `${createdPrefix}，请重试加载新版本。`
    : rebuildErrorText(error);
}

/** 按最新行程生成版本前，展示精确价格复用预览并要求用户显式确认。 */
export function QuoteRebuildFromItineraryDialog({
  visible,
  preview,
  loadingPreview,
  previewError,
  creating,
  createError,
  hasCreatedVersion,
  hasStartedRequest,
  fixedReuseExactMatchedPrices,
  blockedReason,
  onRetryPreview,
  onConfirm,
  onCancel,
}: QuoteRebuildFromItineraryDialogProps) {
  const [reuseExactMatchedPrices, setReuseExactMatchedPrices] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [retryingPreview, setRetryingPreview] = useState(false);
  const priceReuseSelectionInitializedRef = useRef(false);
  const confirmAttemptRef = useRef(0);
  const activeConfirmAttemptRef = useRef<number | null>(null);
  const previewAttemptRef = useRef(0);
  const activePreviewAttemptRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (!visible) {
      priceReuseSelectionInitializedRef.current = false;
      return;
    }
    if (fixedReuseExactMatchedPrices !== null) {
      setReuseExactMatchedPrices(fixedReuseExactMatchedPrices);
      priceReuseSelectionInitializedRef.current = true;
      return;
    }
    if (loadingPreview || !preview || priceReuseSelectionInitializedRef.current) return;
    setReuseExactMatchedPrices(preview.matchedCount > 0);
    priceReuseSelectionInitializedRef.current = true;
  }, [fixedReuseExactMatchedPrices, loadingPreview, preview, visible]);
  useEffect(() => {
    if (visible) return;
    confirmAttemptRef.current += 1;
    activeConfirmAttemptRef.current = null;
    previewAttemptRef.current += 1;
    activePreviewAttemptRef.current = null;
    setConfirming(false);
    setRetryingPreview(false);
  }, [visible]);

  const selectedReuseExactMatchedPrices = fixedReuseExactMatchedPrices ?? reuseExactMatchedPrices;
  const createErrorText = hasCreatedVersion
    ? createdVersionLoadErrorText(createError)
    : rebuildErrorText(createError);
  const previewErrorText = rebuildErrorText(previewError);
  const confirmBusy = creating || confirming;
  const previewBusy = loadingPreview || retryingPreview;
  const previewBlocksConfirm = !hasStartedRequest
    && !hasCreatedVersion
    && (previewBusy || Boolean(previewError));
  const confirmDisabled = confirmBusy || previewBlocksConfirm || Boolean(blockedReason);
  const confirmLabel = hasCreatedVersion
    ? "重试加载新版本"
    : (createError as { code?: unknown } | null)?.code === "QUOTE_BUDGET_VERSION_CONFLICT"
      ? "重新生成"
      : "生成新版本";

  /** 在父状态回填前先用本地锁阻止双击创建或双击预载。 */
  async function confirmRebuild() {
    if (activeConfirmAttemptRef.current !== null || creating || previewBlocksConfirm || blockedReason) return;
    const attempt = ++confirmAttemptRef.current;
    activeConfirmAttemptRef.current = attempt;
    setConfirming(true);
    try {
      await onConfirm(selectedReuseExactMatchedPrices);
    } catch {
      // 错误由 createError 统一展示。
    } finally {
      if (activeConfirmAttemptRef.current === attempt) {
        activeConfirmAttemptRef.current = null;
        setConfirming(false);
      }
    }
  }

  /** 预览重试同样需要同步锁，避免父 loading 状态回填前重复请求。 */
  async function retryPreview() {
    if (activePreviewAttemptRef.current !== null || loadingPreview) return;
    const attempt = ++previewAttemptRef.current;
    activePreviewAttemptRef.current = attempt;
    setRetryingPreview(true);
    try {
      await onRetryPreview();
    } catch {
      // 错误由 previewError 统一展示。
    } finally {
      if (activePreviewAttemptRef.current === attempt) {
        activePreviewAttemptRef.current = null;
        setRetryingPreview(false);
      }
    }
  }

  return (
    <Modal
      visible={visible}
      title="生成最新行程预算版本"
      style={{ width: 560, maxWidth: "calc(100vw - 32px)" }}
      footer={(
        <>
          <Button disabled={confirmBusy} onClick={onCancel}>取消</Button>
          <Button
            type="primary"
            loading={confirmBusy}
            disabled={confirmDisabled}
            onClick={() => void confirmRebuild()}
          >
            {confirmLabel}
          </Button>
        </>
      )}
      maskClosable={!confirmBusy}
      closable={!confirmBusy}
      escToExit={!confirmBusy}
      autoFocus
      focusLock
      onCancel={() => {
        if (!confirmBusy) onCancel();
      }}
    >
      <p>新版本以当前行程重新生成数量和资源结构，旧版本保持不变。</p>
      {!hasCreatedVersion && previewBusy ? <div role="status">正在计算可复用价格…</div> : null}
      {!hasCreatedVersion && previewErrorText ? (
        <div role="alert" style={{ overflowWrap: "anywhere" }}>
          价格复用预览失败：{previewErrorText}
          <Button type="text" loading={retryingPreview} disabled={previewBusy} onClick={() => void retryPreview()}>
            重试价格预览
          </Button>
        </div>
      ) : null}
      {blockedReason ? <div role="alert">{blockedReason}</div> : null}
      {!hasCreatedVersion && preview ? (
        <>
          <p>可精确复用 {preview.matchedCount} 条价格</p>
          <p>
            未匹配 {preview.unmatchedCount} 条；身份歧义 {preview.ambiguousCount} 条。歧义行不会自动复用。
          </p>
          {preview.unmatchedCount + preview.ambiguousCount > 0 ? <p role="note">未匹配或有歧义的明细不沿用当前人工价格，按最新行程生成的价格或系统计价规则计算，请逐项核对。</p> : null}
          {[
            { label: "未匹配价格明细", items: preview.unmatchedItems, count: preview.unmatchedCount },
            { label: "身份歧义价格明细", items: preview.ambiguousItems, count: preview.ambiguousCount },
          ].map((group) => group.count > 0 ? (
            <section key={group.label} aria-label={group.label}>
              <strong>{group.label}</strong>
              {group.items?.length ? <ul aria-label={group.label}>
                {group.items.map((item) => <li key={`${item.targetLineNo}:${item.reasonCode}`} style={{ overflowWrap: "anywhere" }}>
                  <span>第 {item.targetLineNo} 行 · {item.resourceType} · {item.itemNameSnapshot || "未命名明细"}{item.itemSpecSnapshot ? ` · ${item.itemSpecSnapshot}` : ""}{item.travelerRole ? ` · ${item.travelerRole}` : ""}</span>
                  <p>{item.reason}</p>
                </li>)}
              </ul> : <p>当前预览未返回逐项原因，请重试价格预览后核对。</p>}
            </section>
          ) : null)}
          <Checkbox
            checked={selectedReuseExactMatchedPrices}
            disabled={preview.matchedCount === 0 || hasStartedRequest}
            onChange={setReuseExactMatchedPrices}
          >
            复用 {preview.matchedCount} 条精确匹配价格
          </Checkbox>
          {preview.items.length > 0 ? (
            <ul aria-label="精确价格复用明细">
              {preview.items.map((item) => (
                <li
                  key={`${item.targetLineNo}:${item.matchIdentityType}`}
                  style={{ minWidth: 0, overflowWrap: "anywhere" }}
                >
                  {item.resourceType} · {item.itemNameSnapshot}
                  {item.itemSpecSnapshot ? ` · ${item.itemSpecSnapshot}` : ""}
                  {item.travelerRole ? ` · ${item.travelerRole}` : ""}
                  {`：${item.currentUnitPrice} → ${item.sourceUnitPrice} 元/${item.unit}`}
                </li>
              ))}
            </ul>
          ) : null}
        </>
      ) : null}
      {createErrorText ? <div role="alert" style={{ overflowWrap: "anywhere" }}>{createErrorText}</div> : null}
    </Modal>
  );
}
